import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Search, Clock, Edit } from "lucide-react";
import { handleGet, handleRemove } from "../../../controllers/controller.all";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

export default function PeriodList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    
    const [periods, setPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("date_deb");
    const [sortOrder, setSortOrder] = useState("desc");
    const itemsPerPage = 20;

    const fetchPeriods = async () => {
        setLoading(true);
        await handleGet(`/period?ecole=${ecoleId}&skip=0&limit=100000`, (res) => {
            if (res?.periods) {
                setPeriods(res.periods);
                
            }
        }, token);
        setLoading(false);
    };

    useEffect(() => {
        if(token) fetchPeriods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);
    
    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const handleDelete = async (id) => {
        try {
            await handleRemove(`/period/${id}`, fetchPeriods, token, {
                message: "Voulez-vous vraiment supprimer cette période ?"
            });
        } catch (error) {
            console.error(error);
        }
    };


    const filtered_periods = periods.filter(period => {
        const s = "";
        // Dynamic search across string values
        return Object.values(period).some(val => 
            typeof val === 'string' && val.toLowerCase().includes(s)
        ) || (period.nom && period.nom.toLowerCase().includes(s)) ||
             (period.libelle && period.libelle.toLowerCase().includes(s)) ||
             (period.titre && period.titre.toLowerCase().includes(s)) ||
             (period.prenom && period.prenom.toLowerCase().includes(s));
    });

    const sorted_periods = [...filtered_periods].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        // Handle nested objects commonly used for sorting like a.classe.libelle
        if (typeof valA === 'object' && valA !== null) {
            valA = valA.libelle || valA.nom || valA.prenom || "";
        }
        if (typeof valB === 'object' && valB !== null) {
            valB = valB.libelle || valB.nom || valB.prenom || "";
        }

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginated_periods = sorted_periods.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (

        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Périodes</h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez les semestres, trimestres de l'établissement.</p>
                </div>
                <div className="flex gap-3">
                    <Link 
                        to="/periods/create" 
                        className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                    >
                        <Plus className="h-4 w-4" />
                        Nouvelle Période
                    </Link>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="relative max-w-sm w-full text-gray-400">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" />
                        <input 
                            type="text" 
                            placeholder="Rechercher..." 
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <SortableHeader label="Libellé" sortKey="libelle" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Date de début" sortKey="date_deb" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <SortableHeader label="Date de fin" sortKey="date_fin" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : paginated_periods.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">Aucune période trouvée.</td>
                                </tr>
                            ) : (
                                paginated_periods.map((period) => (
                                    <tr key={period._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                                                    <Clock className="w-4 h-4" />
                                                </div>
                                                <div className="text-sm font-medium text-gray-900">{period.libelle}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{period.date_deb}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{period.date_fin}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-end gap-2">
                                            <Link 
                                                to={`/period/update/${period._id}`}
                                                className="text-blue-500 hover:text-blue-700 bg-blue-50 p-2 rounded-lg transition"
                                                title="Modifier la période"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Link>
                                            <button 
                                                onClick={() => handleDelete(period._id)}
                                                className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                title="Supprimer la période"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filtered_periods.length > itemsPerPage && (
                    <Pagination 
                        currentPage={currentPage} 
                        totalItems={filtered_periods.length} 
                        itemsPerPage={itemsPerPage} 
                        onPageChange={setCurrentPage} 
                    />
                )}
            </div>
        </div>
    );
}
